import React, { useCallback, useEffect, useState } from 'react';
import { CreditCard, RefreshCw, Gift, RotateCcw, Undo2, X, ExternalLink } from 'lucide-react';
import { vendorSubscriptionService } from '../services/vendorSubscription.service';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useDialog } from '../contexts/DialogContext';

const PLANS = ['', 'basic', 'pro'];
const STATUSES = ['', 'active', 'trialing', 'past_due', 'incomplete', 'canceled', 'unpaid'];

const planClass = (plan) =>
    plan === 'pro'
        ? 'bg-green-100 text-green-700 border-green-300'
        : 'bg-gray-100 text-gray-600 border-gray-300';

const statusClass = (s) => {
    switch (s) {
        case 'active':
        case 'trialing':
            return 'bg-green-100 text-green-700 border-green-300';
        case 'past_due':
        case 'incomplete':
            return 'bg-amber-100 text-amber-800 border-amber-300';
        case 'canceled':
        case 'unpaid':
        case 'incomplete_expired':
            return 'bg-red-100 text-red-700 border-red-300';
        default:
            return 'bg-gray-100 text-gray-600 border-gray-300';
    }
};

const moneyMinor = (v, ccy = 'NZD') =>
    v == null ? '—' : `${ccy.toUpperCase()} $${(Number(v) / 100).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString();
    } catch {
        return iso;
    }
};

const VendorSubscriptions = () => {
    const { toast, confirm, prompt } = useDialog();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [filters, setFilters] = useState({ plan: '', status: '', q: '' });
    const [detail, setDetail] = useState(null);
    const [detailBusy, setDetailBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setMessage('');
        try {
            const res = await vendorSubscriptionService.list({
                plan: filters.plan || undefined,
                status: filters.status || undefined,
                q: filters.q || undefined,
                limit: 100,
            });
            setRows(Array.isArray(res?.items) ? res.items : []);
        } catch (e) {
            setMessage(e?.message || e?.response?.data?.detail || 'Failed to load subscriptions');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        load();
    }, [load]);

    const openDetail = async (vendorId) => {
        setDetailBusy(true);
        try {
            const res = await vendorSubscriptionService.detail(vendorId);
            setDetail(res);
        } catch (e) {
            setMessage(e?.message || e?.response?.data?.detail || 'Failed to load detail');
        } finally {
            setDetailBusy(false);
        }
    };

    const refreshDetail = () => detail && openDetail(detail.vendor.id);

    const doGrantComp = async (vendorId) => {
        const values = await prompt({
            title: 'Grant complimentary Pro',
            confirmLabel: 'Grant',
            fields: [
                { key: 'until', label: 'Comp until (YYYY-MM-DD)', placeholder: 'Leave blank for open-ended' },
                { key: 'reason', label: 'Reason', defaultValue: 'Goodwill' },
            ],
        });
        if (!values) return;
        try {
            await vendorSubscriptionService.grantComp(vendorId, { until: values.until || null, reason: values.reason || '' });
            await Promise.all([load(), refreshDetail()]);
            toast.success('Complimentary Pro granted.');
        } catch (e) {
            toast.error(e?.message || e?.response?.data?.detail || 'Grant failed');
        }
    };

    const doRevokeComp = async (vendorId) => {
        const ok = await confirm({
            title: 'Revoke complimentary Pro',
            message: 'Revoke complimentary Pro? Vendor drops to Basic.',
            confirmLabel: 'Revoke',
        });
        if (!ok) return;
        try {
            await vendorSubscriptionService.revokeComp(vendorId);
            await Promise.all([load(), refreshDetail()]);
            toast.success('Complimentary Pro revoked.');
        } catch (e) {
            toast.error(e?.message || e?.response?.data?.detail || 'Revoke failed');
        }
    };

    const doRefund = async (vendorId) => {
        const values = await prompt({
            title: 'Refund last paid invoice',
            confirmLabel: 'Refund',
            danger: true,
            fields: [
                { key: 'amount', label: 'Refund amount in dollars', placeholder: 'e.g. 25.00', required: true, type: 'number' },
                { key: 'reason', label: 'Reason', defaultValue: 'Manual adjustment' },
            ],
        });
        if (!values) return;
        const amount_minor = Math.round(parseFloat(values.amount) * 100);
        if (!amount_minor || amount_minor <= 0) {
            toast.error('Enter a positive amount');
            return;
        }
        try {
            const res = await vendorSubscriptionService.refund(vendorId, { amount_minor, reason: values.reason || '' });
            toast.success(`Refunded ${moneyMinor(res?.refunded_minor)}`);
            await Promise.all([load(), refreshDetail()]);
        } catch (e) {
            toast.error(e?.message || e?.response?.data?.detail || 'Refund failed');
        }
    };

    const runMaintenance = async () => {
        try {
            const res = await vendorSubscriptionService.runMaintenance();
            toast.success(
                `Maintenance: ${res?.downgraded_past_due || 0} past-due downgraded, ` +
                    `${res?.comp_expired || 0} comp expired, ${res?.dunning_sent || 0} reminders sent`,
            );
            load();
        } catch (e) {
            toast.error(e?.message || e?.response?.data?.detail || 'Maintenance run failed');
        }
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <CreditCard className="w-6 h-6 text-gray-700" />
                    <h1 className="text-xl font-semibold">Vendor Subscriptions</h1>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={runMaintenance}
                        className="text-sm px-3 py-1.5 border rounded-lg hover:bg-gray-50 flex items-center gap-1"
                        title="Run the dunning / grace / comp-expiry sweep now"
                    >
                        <RotateCcw className="w-4 h-4" /> Run maintenance
                    </button>
                    <button
                        onClick={load}
                        className="text-sm px-3 py-1.5 border rounded-lg hover:bg-gray-50 flex items-center gap-1"
                    >
                        <RefreshCw className="w-4 h-4" /> Refresh
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
                <select
                    value={filters.plan}
                    onChange={(e) => setFilters((f) => ({ ...f, plan: e.target.value }))}
                    className="text-sm border rounded-lg px-2 py-1.5"
                >
                    {PLANS.map((p) => (
                        <option key={p || 'all'} value={p}>
                            {p ? `Plan: ${p}` : 'All plans'}
                        </option>
                    ))}
                </select>
                <select
                    value={filters.status}
                    onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                    className="text-sm border rounded-lg px-2 py-1.5"
                >
                    {STATUSES.map((s) => (
                        <option key={s || 'all'} value={s}>
                            {s ? `Status: ${s}` : 'All statuses'}
                        </option>
                    ))}
                </select>
                <input
                    value={filters.q}
                    onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                    placeholder="Search vendor name / code"
                    className="text-sm border rounded-lg px-3 py-1.5 w-64"
                />
            </div>

            {message && <div className="mb-3 text-sm text-red-600">{message}</div>}

            <PlanPricingCard />

            {loading ? (
                <LoadingSpinner />
            ) : (
                <div className="overflow-x-auto border rounded-xl">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500">
                            <tr>
                                <th className="text-left px-3 py-2">Vendor</th>
                                <th className="text-left px-3 py-2">Plan</th>
                                <th className="text-left px-3 py-2">Status</th>
                                <th className="text-left px-3 py-2">Comp</th>
                                <th className="text-left px-3 py-2">Auto-renew</th>
                                <th className="text-left px-3 py-2">Period end</th>
                                <th className="text-right px-3 py-2">Amount / cycle</th>
                                <th className="text-right px-3 py-2">Lifetime revenue</th>
                                <th className="px-3 py-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.vendor_id} className="border-t hover:bg-gray-50">
                                    <td className="px-3 py-2">
                                        <div className="font-medium">{r.vendor_name}</div>
                                        <div className="text-xs text-gray-400">{r.vendor_code}</div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`text-xs px-2 py-0.5 rounded border ${planClass(r.plan)}`}>
                                            {r.plan}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`text-xs px-2 py-0.5 rounded border ${statusClass(r.status)}`}>
                                            {r.status}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">{r.comp ? 'Yes' : '—'}</td>
                                    <td className="px-3 py-2">
                                        {r.plan === 'pro' ? (r.auto_renew === false || r.cancel_at_period_end ? 'Ending' : 'On') : '—'}
                                    </td>
                                    <td className="px-3 py-2">{fmtDate(r.current_period_end || r.started_at)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {r.plan === 'pro' ? moneyMinor(r.amount_minor, r.currency || 'NZD') : '—'}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {r.plan === 'pro'
                                            ? moneyMinor(r.lifetime_revenue_minor, r.currency || 'NZD')
                                            : '—'}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <button
                                            onClick={() => openDetail(r.vendor_id)}
                                            className="text-xs px-2 py-1 border rounded hover:bg-gray-100"
                                        >
                                            Open
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {!rows.length && (
                                <tr>
                                    <td colSpan={9} className="px-3 py-6 text-center text-gray-400">
                                        No subscriptions
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {detail && (
                <div className="fixed inset-0 bg-black/30 flex justify-end z-50" onClick={() => setDetail(null)}>
                    <div
                        className="w-full max-w-lg bg-white h-full overflow-y-auto p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h2 className="text-lg font-semibold">{detail.vendor.name}</h2>
                                <div className="text-xs text-gray-400">{detail.vendor.code}</div>
                            </div>
                            <button onClick={() => setDetail(null)} className="p-1 hover:bg-gray-100 rounded">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {detailBusy ? (
                            <LoadingSpinner />
                        ) : (
                            <>
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    <Field label="Plan" value={detail.subscription.plan} />
                                    <Field label="Status" value={detail.subscription.status} />
                                    <Field
                                        label="Amount / cycle"
                                        value={moneyMinor(detail.subscription.amount_minor, detail.subscription.currency)}
                                    />
                                    <Field label="Period start" value={fmtDate(detail.subscription.current_period_start)} />
                                    <Field label="Period end" value={fmtDate(detail.subscription.current_period_end)} />
                                    <Field
                                        label="Lifetime revenue"
                                        value={moneyMinor(detail.lifetime_revenue_minor ?? detail.subscription.amount_minor, detail.subscription.currency)}
                                    />
                                    <Field
                                        label="Auto-renew"
                                        value={detail.subscription.auto_renew ? 'Yes' : 'No'}
                                    />
                                    <Field
                                        label="Comp"
                                        value={
                                            detail.subscription.comp
                                                ? `Yes${detail.subscription.comp_until ? ` (until ${fmtDate(detail.subscription.comp_until)})` : ''}`
                                                : '—'
                                        }
                                    />
                                </div>

                                {detail.stripe?.customer_id && (
                                    <a
                                        href={`https://dashboard.stripe.com/customers/${detail.stripe.customer_id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 text-xs text-blue-600 mb-4"
                                    >
                                        <ExternalLink className="w-3 h-3" /> Stripe customer {detail.stripe.customer_id}
                                    </a>
                                )}

                                <div className="flex flex-wrap gap-2 mb-5">
                                    {detail.subscription.comp ? (
                                        <button
                                            onClick={() => doRevokeComp(detail.vendor.id)}
                                            className="text-xs px-3 py-1.5 border rounded-lg hover:bg-gray-50 flex items-center gap-1"
                                        >
                                            <Undo2 className="w-3 h-3" /> Revoke comp
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => doGrantComp(detail.vendor.id)}
                                            className="text-xs px-3 py-1.5 border rounded-lg hover:bg-gray-50 flex items-center gap-1"
                                        >
                                            <Gift className="w-3 h-3" /> Grant comp Pro
                                        </button>
                                    )}
                                    <button
                                        onClick={() => doRefund(detail.vendor.id)}
                                        className="text-xs px-3 py-1.5 border rounded-lg hover:bg-gray-50 flex items-center gap-1"
                                    >
                                        <RotateCcw className="w-3 h-3" /> Manual refund
                                    </button>
                                </div>

                                <h3 className="text-sm font-semibold mb-2">Event history</h3>
                                <div className="border rounded-lg overflow-hidden">
                                    <table className="min-w-full text-xs">
                                        <thead className="bg-gray-50 text-gray-500">
                                            <tr>
                                                <th className="text-left px-2 py-1.5">When</th>
                                                <th className="text-left px-2 py-1.5">Event</th>
                                                <th className="text-right px-2 py-1.5">Amount</th>
                                                <th className="text-right px-2 py-1.5">Refund</th>
                                                <th className="text-left px-2 py-1.5">Note</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(detail.events || []).map((e) => (
                                                <tr key={e.id} className="border-t">
                                                    <td className="px-2 py-1.5 whitespace-nowrap">
                                                        {e.created_at ? new Date(e.created_at).toLocaleString() : '—'}
                                                    </td>
                                                    <td className="px-2 py-1.5">{e.event}</td>
                                                    <td className="px-2 py-1.5 text-right">
                                                        {e.amount_minor == null ? '—' : moneyMinor(e.amount_minor)}
                                                    </td>
                                                    <td className="px-2 py-1.5 text-right">
                                                        {e.refund_minor == null ? '—' : moneyMinor(e.refund_minor)}
                                                    </td>
                                                    <td className="px-2 py-1.5 text-gray-500">{e.note}</td>
                                                </tr>
                                            ))}
                                            {!detail.events?.length && (
                                                <tr>
                                                    <td colSpan={5} className="px-2 py-4 text-center text-gray-400">
                                                        No events
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const Field = ({ label, value }) => (
    <div>
        <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
        <div className="text-sm">{value ?? '—'}</div>
    </div>
);

const PlanPricingCard = () => {
    const { toast } = useDialog();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [enabled, setEnabled] = useState(true);
    const [monthly, setMonthly] = useState('20.00');
    const [cycleMonths, setCycleMonths] = useState('3');
    const [currency, setCurrency] = useState('nzd');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await vendorSubscriptionService.getConfig();
            const e = res?.effective || {};
            setEnabled(e.enabled !== false);
            setMonthly(((Number(e.pro_monthly_minor) || 0) / 100).toFixed(2));
            setCycleMonths(String(e.cycle_months || 3));
            setCurrency(String(e.currency || 'nzd').toLowerCase());
        } catch (err) {
            toast.error(err?.message || 'Failed to load plan pricing');
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        load();
    }, [load]);

    const months = Math.max(1, parseInt(cycleMonths, 10) || 1);
    const monthlyMinor = Math.round(parseFloat(monthly) * 100) || 0;
    const cycleTotal = monthlyMinor * months;
    const ccy = (currency || 'nzd').toUpperCase();

    const save = async (ev) => {
        ev.preventDefault();
        if (monthlyMinor <= 0) {
            toast.error('Enter a Pro monthly price greater than zero');
            return;
        }
        if (months < 1 || months > 24) {
            toast.error('Billing cycle must be between 1 and 24 months');
            return;
        }
        setSaving(true);
        try {
            await vendorSubscriptionService.putConfig({
                data: {
                    enabled,
                    pro_monthly_minor: monthlyMinor,
                    cycle_months: months,
                    currency: currency.trim().toLowerCase() || 'nzd',
                },
            });
            toast.success('Plan pricing saved. New upgrades will use this amount.');
            await load();
        } catch (err) {
            toast.error(err?.message || 'Failed to save plan pricing');
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={save} className="mb-5 border rounded-xl p-4 bg-white">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <h2 className="text-sm font-semibold">Plan pricing</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Vendors see this price. Stripe only collects the card — no Stripe Price id required.
                    </p>
                </div>
                {loading ? <span className="text-xs text-gray-400">Loading…</span> : null}
            </div>
            <div className="flex flex-wrap items-end gap-4">
                <label className="flex items-center gap-2 text-sm pb-1.5">
                    <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) => setEnabled(e.target.checked)}
                    />
                    Subscriptions enabled
                </label>
                <label className="text-sm">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
                        Monthly price
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="text-gray-500">{ccy}</span>
                        <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={monthly}
                            onChange={(e) => setMonthly(e.target.value)}
                            className="text-sm border rounded-lg px-2 py-1.5 w-28"
                        />
                    </div>
                </label>
                <label className="text-sm">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
                        Billed every
                    </div>
                    <div className="flex items-center gap-1">
                        <input
                            type="number"
                            min="1"
                            max="24"
                            value={cycleMonths}
                            onChange={(e) => setCycleMonths(e.target.value)}
                            className="text-sm border rounded-lg px-2 py-1.5 w-16"
                        />
                        <span className="text-gray-500">months</span>
                    </div>
                </label>
                <label className="text-sm">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
                        Currency
                    </div>
                    <input
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                        maxLength={3}
                        className="text-sm border rounded-lg px-2 py-1.5 w-20 uppercase"
                    />
                </label>
                <button
                    type="submit"
                    disabled={saving || loading}
                    className="text-sm px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                    {saving ? 'Saving…' : 'Save price'}
                </button>
            </div>
            <p className="text-xs text-gray-500 mt-3">
                Charge per cycle: {moneyMinor(cycleTotal, ccy)} (ex GST). Vendors see GST
                (15%) added at checkout; Stripe collects the total. Already-active Pro
                vendors keep their current rate until they renew or re-subscribe.
            </p>
        </form>
    );
};

export default VendorSubscriptions;
